import styled, { css } from 'styled-components'

import { media } from '../../../utils/mediaQuery'

export const LegendPanelContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{
  $active: boolean
  $position?: {
    top?: string
    right?: string
    bottom?: string
    left?: string
  }
  $width?: string
  $zIndex?: number
}>`
  position: absolute;
  ${({ $position }) =>
    $position
      ? css`
          ${$position.top && `top: ${$position.top};`}
          ${$position.right && `right: ${$position.right};`}
          ${$position.bottom && `bottom: ${$position.bottom};`}
          ${$position.left && `left: ${$position.left};`}
        `
      : css`
          bottom: 16px;
          left: 16px;
        `}
  width: ${({ $width }) => $width || '280px'};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 16px;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transform: translateY(${({ $active }) => ($active ? '0' : '16px')});
  transition: all 0.3s ease-in-out;
  z-index: ${({ $zIndex }) => $zIndex || 10};

  ${media.tabletDown} {
    width: calc(100vw - 32px);
    max-width: 280px;
  }
`

export const LegendHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

export const LegendTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex: 1;
`

export const LegendClose = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: 8px;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

export const LegendContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const LegendGradient = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $colors: string[] }>`
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(to right, ${({ $colors }) => $colors.join(', ')});
  margin: 8px 0;
`

export const LegendLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const LegendUnit = styled.div`
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 4px;
`

export const LegendCategories = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const LegendCategoryItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      &:hover {
        background: ${theme.colors.background.secondary};
      }
    `}
`

export const LegendColorBox = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const LegendLabel = styled.span.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $active?: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  opacity: ${({ $active }) => ($active === false ? 0.5 : 1)};
`
